import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dices, History, Ticket } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Card } from "@/components/ranger/PageHeader";
import { ConfigForm } from "@/components/ranger/Crud";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";
import { BTN_SECONDARY } from "@/pages/Servers";

const GAMES = [["coinflip", "Coinflip"], ["dice", "Dice"], ["overunder", "Over / Under"], ["roulette", "Roulette"], ["blackjack", "Blackjack"], ["mines", "Mines"], ["slots", "Slots"], ["scratch", "Scratch cards"], ["crash", "Crash"], ["lottery", "Lottery"], ["stocks", "Stock market"]];
const FIELDS = [
  { key: "enabled", label: "Casino enabled", type: "switch" },
  { key: "min_bet", label: "Minimum bet", type: "number" },
  { key: "max_bet", label: "Maximum bet", type: "number" },
  { key: "games", label: "Games available", type: "multi", options: GAMES },
  { key: "lottery_ticket_price", label: "Lottery ticket price", type: "number" },
  { key: "lottery_house_pct", label: "Lottery house cut (%)", type: "number", hint: "Kept from the pot; the rest goes to the winner (0-50)." },
  { key: "lottery_channel_id", label: "Lottery results channel ID", hint: "Blank = leaderboard channel. Draws run Saturdays 8pm US Central." },
  { key: "stock_fee_pct", label: "Stock trade fee (%)", type: "number", hint: "Charged on every buy and sell (0-10)." },
];

const LotteryAdmin = ({ cid }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/communities/${cid}/lottery`).then((r) => setD(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);
  const drawNow = async () => { if (!window.confirm("Draw the lottery now?")) return; try { const { data } = await api.post(`/communities/${cid}/lottery/draw`); toast.success(`${data.winner} won ${data.prize}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  return (
    <Card title="Lottery" icon={Ticket} testId="lottery-admin-card" className="mb-5">
      <p className="text-sm text-slate-300">Prize <b data-testid="lottery-admin-prize">{d.prize}</b> (pot {d.pot}) · {d.tickets} tickets from {d.players} players{d.last && ` · last winner ${d.last.winner} (${d.last.prize})`}</p>
      <button data-testid="lottery-draw-now" onClick={drawNow} disabled={!d.tickets} className={`${BTN_SECONDARY} mt-3`}>Draw now</button>
    </Card>
  );
};

export default function Casino() {
  const { active } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/communities/${active.id}/casino/history`).then((r) => setD(r.data)).catch(() => {}); }, [active.id]);
  const s = d?.summary;
  return (
    <div>
      <PageHeader eyebrow="Economy" title="Casino" description="Wallet-only games with a small house edge. Bets are taken before the result is drawn, using a secure random generator." />
      <ConfigForm cid={active.id} field="casino" fields={FIELDS} title="Casino settings" icon={Dices} />
      <LotteryAdmin cid={active.id} />
      {s && <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">{[["Games", s.games], ["Wagered", s.wagered], ["Paid out", s.paid_out], ["House net", s.house_net]].map(([k, v]) => (
        <Card key={k}><div className="rg-label">{k}</div><div data-testid={`casino-${k.toLowerCase().replace(" ", "-")}`} className="font-display text-3xl font-bold text-slate-100">{v}</div></Card>
      ))}</div>}
      <Card title="Recent games" icon={History} testId="casino-history-card">
        {!d?.history.length ? <p className="text-sm text-slate-500">No games played yet.</p> : (
          <div className="overflow-x-auto"><Table data-testid="casino-history-table">
            <TableHeader><TableRow className="border-[#1E2D4A]">{["Time", "Discord ID", "Game", "Bet", "Payout", "Net"].map((h) => <TableHead key={h} className="rg-label">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{d.history.map((r) => (
              <TableRow key={r.id} className="border-[#1E2D4A]/60 font-mono text-xs">
                <TableCell className="text-slate-400">{fmtTime(r.created_at)}</TableCell><TableCell className="text-slate-400">{r.discord_id}</TableCell>
                <TableCell className="text-slate-200">{r.game}</TableCell><TableCell>{r.bet}</TableCell><TableCell>{r.payout}</TableCell>
                <TableCell className={r.net < 0 ? "text-red-400" : "text-emerald-400"}>{r.net > 0 ? `+${r.net}` : r.net}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </Card>
    </div>
  );
}
